/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { calculateSPLOCICells } from '../../../../utils/ews-sploci-utils';
import { checkSelectedActiveColumn, mappingKhmerNumber } from '../../../../utils/market-development';
import { getCompanyTypes, getYear } from '../../utils';
import Spreadsheet from 'react-spreadsheet';
import EWSLabels from '../ews-template-labels';
import Loading from '../../../../components/Loading';
import useStore from '../../../../store';

const Q_REPORT = gql`
	query getEarlyWarningSPLOCISummaryReport($year: Float!, $companyTypes: [InsuranceInstitutionsType!] = [], $companyId: String) {
		getEarlyWarningSPLOCISummaryReport(year: $year, companyTypes: $companyTypes, companyId: $companyId)
	}
`;

export default function SPLOCISpreadSheetTable({ year = null, companyType = null, companyId = null, onReadyForExport = () => null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState([]);
	const [q1Cells, setQ1Cells] = useState([]);
	const [q2Cells, setQ2Cells] = useState([]);
	const [q3Cells, setQ3Cells] = useState([]);
	const [q4Cells, setQ4Cells] = useState([]);
	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);

	useEffect(load, [year, companyType, companyId]);

	useEffect(() => data?.getEarlyWarningSPLOCISummaryReport && setDetail(data?.getEarlyWarningSPLOCISummaryReport), [data]);

	useEffect(() => detail && quarterDetail(detail), [detail]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					})
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = { year: getYear(year), companyTypes: getCompanyTypes(companyType), companyId };
		loadReport({ variables });
	}

	function quarterDetail() {
		const localQ1Cells = quarterCells({ code: 'SPLOCI', labels: detail?.labels, quarter: detail?.q1 });
		const localQ2Cells = quarterCells({ code: 'SPLOCI', labels: detail?.labels, quarter: detail?.q2 });
		const localQ3Cells = quarterCells({ code: 'SPLOCI', labels: detail?.labels, quarter: detail?.q3 });
		const localQ4Cells = quarterCells({ code: 'SPLOCI', labels: detail?.labels, quarter: detail?.q4 });
		setQ1Cells(localQ1Cells);
		setQ2Cells(localQ2Cells);
		setQ3Cells(localQ3Cells);
		setQ4Cells(localQ4Cells);

		onReadyForExport({ labels: detail?.labels, q1Cells: localQ1Cells, q2Cells: localQ2Cells, q3Cells: localQ3Cells, q4Cells: localQ4Cells });
	}

	function quarterCells({ code = null, labels = [], quarter = null }) {
		const localCells = labels?.map((label) => {
			const values = [
				{ value: 0, readOnly: true },
				{ value: 0, readOnly: true },
			];
			const labelObject = quarter?.find((e) => e?.code === label?.code);
			return labelObject?.values || values;
		});
		const { cells } = calculateSPLOCICells({
			code,
			cells: localCells,
			numLabel: labels?.length,
			readOnly: true,
		});

		const truncatedCells = cells.map((row) =>
			row.map((cell) => {
				const val = cell?.value;
				const newVal = typeof val === 'number' ? truncateDecimal(val, 3) : val;
				return { ...cell, value: newVal };
			})
		);

		return truncatedCells;
	}

	function truncateDecimal(value, digits = 3) {
		if (typeof value !== 'number') return value;
		const factor = 10 ** digits;
		return Math.trunc(value * factor) / factor;
	}

	return (
		<>
			<Box>
				{loading && (
					<>
						<Loading />
					</>
				)}
				{!loading && !detail && (
					<>
						<Text color="gray.400" textAlign="center" mt={4}>
							{t('No Data')}
						</Text>
					</>
				)}
				{!loading && detail && (
					<>
						<Box className="ews-container">
							<div className="ews-2-row-head">
								{t('Summarized Financial Statements Report')}
								<span style={{ marginLeft: '2px' }}>{lang === 'kh' ? mappingKhmerNumber(getYear(year)) : getYear(year)}</span>
							</div>
							<Flex>
								{q1Cells?.length > 0 && (
									<table className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="2" className="ews-background-light-silver">
													{t('q1')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td className={checkSelectedActiveColumn(active, 0 + 0) ? 'active-column' : 'ews-background-light-silver'}>
													{t('USD')}
												</td>
												<td className={checkSelectedActiveColumn(active, 1 + 0) ? 'active-column' : 'ews-background-light-silver'}>
													{t(`KHR'000`)}
												</td>
											</tr>
										</thead>
									</table>
								)}
								{q2Cells?.length > 0 && (
									<table className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="2" className="ews-background-light-silver">
													{t('q2')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td className={checkSelectedActiveColumn(active, 0 + 5) ? 'active-column' : 'ews-background-light-silver'}>
													{t('USD')}
												</td>
												<td className={checkSelectedActiveColumn(active, 1 + 5) ? 'active-column' : 'ews-background-light-silver'}>
													{t(`KHR'000`)}
												</td>
											</tr>
										</thead>
									</table>
								)}
								{q3Cells?.length > 0 && (
									<table className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="2" className="ews-background-light-silver">
													{t('q3')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td className={checkSelectedActiveColumn(active, 0 + 10) ? 'active-column' : 'ews-background-light-silver'}>
													{t('USD')}
												</td>
												<td className={checkSelectedActiveColumn(active, 1 + 10) ? 'active-column' : 'ews-background-light-silver'}>
													{t(`KHR'000`)}
												</td>
											</tr>
										</thead>
									</table>
								)}
								{q4Cells?.length > 0 && (
									<table className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="2" className="ews-background-light-silver">
													{t('q4')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td className={checkSelectedActiveColumn(active, 0 + 15) ? 'active-column' : 'ews-background-light-silver'}>
													{t('USD')}
												</td>
												<td className={checkSelectedActiveColumn(active, 1 + 15) ? 'active-column' : 'ews-background-light-silver'}>
													{t(`KHR'000`)}
												</td>
											</tr>
										</thead>
									</table>
								)}
							</Flex>
							<div>
								<EWSLabels type="EWS" labels={detail?.labels} active={active} />
								<Flex>
									{q1Cells?.length > 0 && (
										<Spreadsheet
											data={q1Cells}
											onChange={(cells) => {
												setQ1Cells(
													q1Cells?.reduce((pre, cur, i) => {
														let localPre = pre;
														localPre.push([
															{ value: cur[0]?.value, readOnly: true },
															{ value: cur[1]?.value, readOnly: true },
														]);
														return localPre;
													}, [])
												);
											}}
											onActivate={(e) => setActive({ ...e, column: e?.column + 0, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									)}
									{q2Cells?.length > 0 && (
										<Spreadsheet
											data={q2Cells}
											onChange={(cells) => {
												setQ2Cells(
													q2Cells?.reduce((pre, cur, i) => {
														let localPre = pre;
														localPre.push([
															{ value: cur[0]?.value, readOnly: true },
															{ value: cur[1]?.value, readOnly: true },
														]);
														return localPre;
													}, [])
												);
											}}
											onActivate={(e) => setActive({ ...e, column: e?.column + 5, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									)}
									{q3Cells?.length > 0 && (
										<Spreadsheet
											data={q3Cells}
											onChange={(cells) => {
												setQ3Cells(
													q3Cells?.reduce((pre, cur, i) => {
														let localPre = pre;
														localPre.push([
															{ value: cur[0]?.value, readOnly: true },
															{ value: cur[1]?.value, readOnly: true },
														]);
														return localPre;
													}, [])
												);
											}}
											onActivate={(e) => setActive({ ...e, column: e?.column + 10, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									)}
									{q4Cells?.length > 0 && (
										<Spreadsheet
											data={q4Cells}
											onChange={(cells) => {
												setQ4Cells(
													q4Cells?.reduce((pre, cur, i) => {
														let localPre = pre;
														localPre.push([
															{ value: cur[0]?.value, readOnly: true },
															{ value: cur[1]?.value, readOnly: true },
														]);
														return localPre;
													}, [])
												);
											}}
											onActivate={(e) => setActive({ ...e, column: e?.column + 15, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									)}
								</Flex>
							</div>
						</Box>
					</>
				)}
			</Box>
		</>
	);
}
